import React, { useEffect, useState } from "react";
import moment from "moment";
/* The ONE antd control left in this band, and only for a merged range: picking a
   window from a single calendar is the whole point of merging, and a native
   <input type="date"> has no range mode - two of them means two calendars, which is
   what "separate" already gives you. Everything else here stays plain HTML. */
import { DatePicker } from "antd";
import {
  buildDatePeriods,
  hasDateRanges,
  periodFilterIds,
  periodName,
  readDateDisplay,
} from "../../../utils/dateFilterDisplay";
import "./widgetFilters.css";

/**
 * Global filters for one resource, drawn above the widgets.
 *
 * Built from PLAIN HTML with `wgf-` prefixed classes - no antd, no design system.
 * That is deliberate and it is what makes this a faithful preview: the host app's
 * own controls are plain too (witmeg-reporting-embed-v2, components/ui/Controls.js),
 * and the sizes, borders and colours below are ported from its controls.css rather
 * than approximated. An antd Input here is a different height, radius and focus ring
 * from what a reader actually gets.
 *
 * These are request parameters: Apply re-queries the resource's API, so row counts
 * and totals change with them. Column filters in the table header are the other
 * mechanism - those only trim rows already loaded.
 */

const hasValue = (v) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);

const DAY = "YYYY-MM-DD";
const REQUEST_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSSZ";
const FLOOR_DATE = "2000-01-01";

/* Rolling windows a date filter's config can name, as the editor writes them. */
const DYNAMIC_WINDOWS = {
  LastWeek: [7, "d"],
  Last2Weeks: [14, "d"],
  LastMonth: [1, "months"],
  LastQuarter: [3, "months"],
  Last6Months: [6, "months"],
  LastYear: [1, "years"],
};

/**
 * What a date filter is allowed to select, as min/max for a native date input.
 *
 * Ported from the package's getDateBounds rather than from this app's getMinDate:
 * the latter treats EVERY non-dynamic range as a minimum, so "Fixed - Before" does
 * the opposite of its name. The package fixed that; matching it here is the point of
 * the preview.
 */
function dateBounds(config) {
  if (!config || !config.range) return { min: FLOOR_DATE, max: moment().format(DAY) };

  if (config.range === "dynamic") {
    const window = DYNAMIC_WINDOWS[config.value];
    // an unrecognised preset must not silently open the picker to everything
    const min = window ? moment().utc().subtract(window[0], window[1]).format(DAY) : FLOOR_DATE;
    return { min, max: moment().format(DAY) };
  }

  const bound = config.value ? moment(config.value).format(DAY) : null;
  if (config.range === "fixedBefore") return { min: null, max: bound };
  if (config.range === "fixedAfter") return { min: bound, max: null };
  return { min: bound || FLOOR_DATE, max: moment().format(DAY) };
}

/**
 * A picked day -> the timestamp the request wants.
 *
 * The end of a range takes the END of its day, so that day is included rather than
 * cut off at midnight. The package decides which end it is by string-matching
 * "EndDate"/"ToDate"; here the range structure says so outright, and the name check
 * is only the fallback for a date filter that is not part of a range.
 */
const toRequestDate = (day, isEnd) => {
  if (!day) return null;
  const m = moment.utc(day, DAY);
  return (isEnd ? m.endOf("day") : m.startOf("day")).format(REQUEST_FORMAT);
};

const looksLikeEnd = (filter) => /^(enddate|todate)$/i.test(String(filter?.parameterName || "").replace(/\s/g, ""));

const asDay = (value) => (value ? moment(value).format(DAY) : "");

/** Whatever is wrong with this filter's value, as a message or null. */
function validateFilter(filter) {
  if (!hasValue(filter.value)) return null;

  const config = filter.config || {};

  if (filter.dataType === "number") {
    const n = Number(filter.value);
    if (Number.isNaN(n)) return "Enter a number.";
    /* The editor's Limit convention: a row cap of 0 or less returns nothing, which
       reads as a broken report rather than an empty filter. */
    if (String(filter.parameterName).toLowerCase() === "limit" && n <= 0) return "Limit must be greater than 0.";
    if (config.min !== null && config.min !== undefined && n < Number(config.min)) return `Minimum is ${config.min}.`;
    if (config.max !== null && config.max !== undefined && n > Number(config.max)) return `Maximum is ${config.max}.`;
  }

  if (filter.dataType === "text" && config.maxLength && String(filter.value).length > Number(config.maxLength)) {
    return `Maximum ${config.maxLength} characters.`;
  }

  if (filter.dataType === "numArray" && filter.value.some((v) => Number.isNaN(Number(v)))) {
    return "Enter numbers separated by commas.";
  }

  return null;
}

/* "FromDate" reads better as "From Date" - the same thing the host app does to turn
   a parameter name into a label. */
const labelFor = (filter) => String(filter?.parameterName || "").replace(/([a-z])([A-Z])/g, "$1 $2");

export default function WidgetGlobalFilters({ resource, onApplyFilter, widgetName }) {
  const [filters, setFilters] = useState(resource?.customFilters || []);
  /* Shown in the band itself rather than as a toast: the band is plain HTML now, and
     a message that appears beside the field it is about is easier to act on than one
     that floats at the top of the page and fades. */
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setFilters(resource?.customFilters || []);
    setMessage(null);
  }, [resource]);

  // No parameters configured means no band at all, as in the host app.
  if (!filters.length) return null;

  /* PRM-517. Date parameters the author grouped into ranges are drawn as ranges -
     one control per period when merged, a labelled From/To pair when separate - and
     everything else stays an ordinary field. A single lone date filter is not a
     range (hasDateRanges) and is left as the plain field it always was. */
  const dateDisplay = readDateDisplay(resource);
  const allPeriods = buildDatePeriods(filters);
  const periods = hasDateRanges(allPeriods) ? allPeriods : [];
  const inRange = periodFilterIds(periods);
  const plainFilters = filters.filter((f) => !inRange.has(f.id));
  const isMerged = dateDisplay.viewBy === "merge";
  const multiPeriod = periods.length > 1;

  const setValues = (patch) =>
    setFilters((current) => current.map((f) => (patch.has(f.id) ? { ...f, value: patch.get(f.id) } : f)));

  const setOne = (filter, value) => setValues(new Map([[filter.id, value]]));

  const activeCount = filters.filter((f) => hasValue(f.value)).length;

  const apply = () => {
    const error = filters.map(validateFilter).find(Boolean);
    if (error) return setMessage(error);
    if (activeCount === 0) return setMessage("Enter a value in at least one filter first.");
    setMessage(null);
    onApplyFilter({ ...resource, customFilters: filters });
  };

  const reset = () => {
    const cleared = (resource?.customFilters || []).map((f) => ({ ...f, value: null }));
    setMessage(null);
    setFilters(cleared);
    onApplyFilter({ ...resource, customFilters: cleared });
  };

  /* ── controls ─────────────────────────────────────────────────────────────── */

  const dateInput = (filter, isEnd) => {
    const { min, max } = dateBounds(filter?.config);
    return (
      <input
        type="date"
        className="wgf-control"
        value={asDay(filter?.value)}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => setOne(filter, toRequestDate(e.target.value, isEnd))}
      />
    );
  };

  const renderInput = (filter) => {
    const config = filter.config || {};
    const invalid = Boolean(validateFilter(filter));
    const className = `wgf-control${invalid ? " wgf-control--invalid" : ""}`;

    switch (filter.dataType) {
      case "date":
        return dateInput(filter, looksLikeEnd(filter));

      case "number":
        return (
          <input
            type="number"
            className={className}
            value={filter.value ?? ""}
            min={config.min}
            max={config.max}
            placeholder={config.min !== undefined && config.max !== undefined ? `${config.min} - ${config.max}` : "Value"}
            onChange={(e) => setOne(filter, e.target.value === "" ? null : Number(e.target.value))}
          />
        );

      case "strArray":
        return (
          <input
            type="text"
            className={className}
            value={Array.isArray(filter.value) ? filter.value.join(",") : (filter.value ?? "")}
            placeholder="value1, value2"
            onChange={(e) =>
              setOne(
                filter,
                e.target.value
                  .split(",")
                  .map((v) => v.trim())
                  .filter((v) => v !== ""),
              )
            }
          />
        );

      case "numArray":
        return (
          <input
            type="text"
            className={className}
            value={Array.isArray(filter.value) ? filter.value.join(",") : (filter.value ?? "")}
            placeholder="1, 2, 3"
            onChange={(e) =>
              setOne(
                filter,
                e.target.value
                  .split(",")
                  .map((v) => v.trim())
                  .filter((v) => v !== "")
                  .map((v) => Number(v)),
              )
            }
          />
        );

      default:
        return (
          <input
            type="text"
            className={className}
            value={filter.value ?? ""}
            maxLength={config.maxLength ? Number(config.maxLength) : undefined}
            placeholder="Value"
            onChange={(e) => setOne(filter, e.target.value === "" ? null : e.target.value)}
          />
        );
    }
  };

  const field = (filter, label) => {
    const error = validateFilter(filter);
    return (
      <div className="wgf-field" key={`wgf_${filter.id}`}>
        <label className="wgf-label">{label ?? labelFor(filter)}</label>
        {renderInput(filter)}
        {error ? <span className="wgf-error">{error}</span> : null}
      </div>
    );
  };

  const renderPeriod = (period, i) => {
    const label = periodName(dateDisplay, i, multiPeriod);

    // An odd date filter out cannot make a range; it falls back to a single field.
    if (period.incomplete) return field(period.from);

    if (isMerged) {
      /* One calendar, both ends: click the start and then the end in the same popup.
         Both values are written in a SINGLE update so the two filters cannot land out
         of step, and the end still takes the end of its day so that day is included. */
      const bounds = dateBounds(period.from.config);
      const min = bounds.min ? moment(bounds.min, DAY) : null;
      const max = bounds.max ? moment(bounds.max, DAY) : null;

      return (
        <div className="wgf-field wgf-field--range" key={`wgf_p_${i}`}>
          <label className="wgf-label">{label}</label>
          <DatePicker.RangePicker
            className="wgf-rangepicker"
            format="DD-MMM-YYYY"
            value={[
              period.from.value ? moment(period.from.value) : null,
              period.to.value ? moment(period.to.value) : null,
            ]}
            disabledDate={(current) => {
              if (!current) return false;
              if (min && current < min.startOf("day")) return true;
              if (max && current > max.endOf("day")) return true;
              return false;
            }}
            onChange={(dates) =>
              setValues(
                new Map([
                  [period.from.id, dates?.[0] ? dates[0].startOf("day").format(REQUEST_FORMAT) : null],
                  [period.to.id, dates?.[1] ? dates[1].endOf("day").format(REQUEST_FORMAT) : null],
                ]),
              )
            }
          />
        </div>
      );
    }

    /* Separate: each end its own labelled field. Several periods get the period name
       above the pair so a reader can tell which window is which. */
    return (
      <div className="wgf-period" key={`wgf_p_${i}`}>
        {multiPeriod ? <span className="wgf-period-name">{label}</span> : null}
        <div className="wgf-period-fields">
          {field(period.from)}
          {field(period.to)}
        </div>
      </div>
    );
  };

  return (
    <div className="wgf-band">
      {/* Only on a board with several widgets, where a reader otherwise cannot tell
          which one these parameters drive. */}
      {widgetName ? <span className="wgf-band-name">{widgetName}</span> : null}

      {/* Fields in their own group so they pack left whatever the band's own
          alignment turns out to be. */}
      <div className="wgf-fields">
        {periods.map(renderPeriod)}
        {plainFilters.map((filter) => field(filter))}
      </div>

      <div className="wgf-actions">
        <button type="button" className="wgf-button wgf-button--secondary" onClick={reset} disabled={activeCount === 0}>
          Reset
        </button>
        <button type="button" className="wgf-button wgf-button--primary" onClick={apply}>
          Apply
        </button>
      </div>

      {message ? <span className="wgf-band-error">{message}</span> : null}
    </div>
  );
}
